import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { calculatePercentage, formatDecimal } from "../../utils";
import { CHART_COLORS } from "./chartTokens";

type ConversionRateCardData = {
  label: "denominator" | "numerator";
  name: string;
  value: number;
};

type ConversionRateCardProps = {
  title: string;
  subtitle?: string;
  label?: string;
  colSpan?: number;
  primaryColor?: string;
  secondaryColor?: string;
  data: [ConversionRateCardData, ConversionRateCardData];
  percentage?: number;
  totalDenominator?: number;
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="rounded-md border border-border bg-card p-3 shadow-md">
        <p className="text-sm font-medium text-palomma-secondary">
          {data.name}: {data.value}
        </p>
      </div>
    );
  }
  return null;
};

const ConversionRateCard = ({
  title,
  subtitle,
  label,
  colSpan,
  primaryColor = CHART_COLORS.primary,
  secondaryColor = CHART_COLORS.neutral,
  data,
  percentage,
  totalDenominator,
}: ConversionRateCardProps) => {
  const denominator = totalDenominator ?? data[0].value + data[1].value;
  const hasNoData = denominator === 0;

  if (hasNoData) {
    return (
      <div style={{ gridColumn: colSpan ? `span ${colSpan}` : undefined }}>
        <div className="flex h-full min-h-[260px] flex-col rounded-2xl border border-border bg-card p-6">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-muted-foreground">Sin datos en este período</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ gridColumn: colSpan ? `span ${colSpan}` : undefined }}>
      <div className="h-full rounded-2xl border border-border bg-card p-6">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>

        <div className="flex justify-center items-center relative mb-4">
          <div className="relative w-full md:w-[250px] h-[220px] md:h-[250px] max-w-[250px] mx-auto">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={90}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  nameKey="name"
                  stroke="none"
                  strokeLinecap="round"
                  isAnimationActive
                  animationDuration={300}
                  cornerRadius={10}
                  paddingAngle={2}
                  activeShape={{ opacity: 0.9 }}
                >
                  <Cell fill={primaryColor} />
                  <Cell fill={secondaryColor} />
                </Pie>
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={false}
                  wrapperStyle={{ zIndex: 1000 }}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Center text */}
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center flex-col pointer-events-none">
              <p className="text-4xl font-bold leading-none">
                {percentage !== undefined
                  ? typeof percentage === "string"
                    ? percentage
                    : formatDecimal(percentage, 0)
                  : calculatePercentage(data[0].value, data[0].value + data[1].value, 1)}
                %
              </p>
              {label && (
                <p className="text-sm text-center mt-1 max-w-[50%]">
                  {label}
                </p>
              )}
            </div>
          </div>
        </div>

        <p className="mb-3 text-center text-sm text-muted-foreground">
          {data[0].value} / {totalDenominator || data[1].value}
        </p>

        <div className="flex items-center justify-center gap-4">
          <div className="flex items-center">
            <div className="mr-2 size-2.5 rounded-full" style={{ backgroundColor: primaryColor }} />
            <p className="text-sm text-palomma-secondary">{data[0].name}</p>
          </div>
          <div className="flex items-center">
            <div className="mr-2 size-2.5 rounded-full" style={{ backgroundColor: secondaryColor }} />
            <p className="text-sm text-muted-foreground">{data[1].name}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionRateCard;
